import { Injectable } from '@angular/core';
import {
    IScene,
    ISceneConfigs,
    ISceneFormControl,
    ISceneInDTO,
    ISceneOutDTO,
    Scene,
} from 'src/app/models/project/scene-model';
import { ITake, ITakeOutDTO } from 'src/app/models/project/take/take-model';
import { TakeConverterService } from './take/take-converter.service';
import { IConvertorAsync } from 'src/app/models/project/project-model';
import {
    DirectionToIconMap,
    ICustomLayout,
    IStagePositionConfigs,
    IStagePositionsExtended,
    IStagePositionWithConfigs,
    StagePositionDirectionsEnum,
    StagePositionMatIcon,
    StagePositionTypeEnum,
} from 'src/app/models/defines';
import { IBasicLottieLayer } from 'src/app/models/lottie/lottie-defines';
import { FormControl } from '@angular/forms';
import {
    IProjectIndexData,
    SharedProjectDBService,
} from 'src/app/services/state-management/project/shared-project-indexdb.service';

@Injectable({
    providedIn: 'root',
})
export class SceneConverterService implements IConvertorAsync<ISceneInDTO, IScene, ISceneOutDTO> {
    constructor(private takeConvertor: TakeConverterService) {}

    async inToLocalAsync(
        inScene: ISceneInDTO,
        baseDesignPath: string,
        waitForLocalVideos: boolean,
        projectId: string,
        projectDBData: IProjectIndexData | null,
        sceneIndex: number,
        streamId?: string
    ) {
        if (!inScene) {
            console.error(`Cannot convert scene  to out dto scene because it's null.`);
            return;
        }
        const basicLottieLayers: IBasicLottieLayer[] = inScene.composition.layouts.map((layout) => {
            const basicLottieLayer: IBasicLottieLayer = {
                lottieId: layout._id,
                lottieJsonPath: layout.lottiePath,
            };
            return basicLottieLayer;
        });
        let selectedTakeId = inScene.selectedTakeId;
        const takes = await Promise.all(
            inScene.takes.map((inTake) =>
                this.takeConvertor.inToLocalAsync(
                    inTake,
                    basicLottieLayers,
                    inScene.composition.layouts[0],
                    waitForLocalVideos,
                    projectId,
                    inScene.id,
                    inScene.name,
                    inScene.isGraphicScene,
                    projectDBData,
                    inTake.id === selectedTakeId
                )
            )
        );
        let chosenTake: ITake;
        /// If a selected take was not initialized, we take the last take in the array
        if (!selectedTakeId) {
            chosenTake = takes[takes.length - 1];
            selectedTakeId = chosenTake.id;
        } else {
            chosenTake = takes.find((take) => take.id === selectedTakeId);
        }

        const sceneFormControls: ISceneFormControl = {
            script: new FormControl<string>(inScene.copy.script),
            title: new FormControl<string>(inScene.name),
        };
        const stagePositions = this.setNewStagePositionsWithConfigs(inScene.stagePositions);
        const scene: ISceneConfigs = {
            id: inScene.id,
            composition: inScene.composition,
            takes: takes,
            selectedTakeId: selectedTakeId,
            copy: inScene.copy,
            chosenTake: chosenTake,
            isHidden: inScene.isHidden,
            name: inScene.name,
            formControls: sceneFormControls,
            status: inScene.status,
            stagePositions: stagePositions,
            isGraphicScene: inScene.isGraphicScene,
            launchIsRecordingNeeded: inScene.launchIsRecordingNeeded,
            number: sceneIndex + 1,
        };

        const newScene = new Scene(scene);

        return newScene;
    }

    localToOut(scene: IScene) {
        if (!scene) {
            console.error(`Cannot convert scene  to out dto scene because it's null.`);
            return;
        }
        const outDTOTakes: ITakeOutDTO[] = scene.takes?.map((take) => this.takeConvertor.localToOut(take));
        const outDTOScene: ISceneOutDTO = {
            id: scene.id,
            composition: scene.composition,
            takes: outDTOTakes ?? [],
            selectedTakeId: scene.selectedTakeId,
            isHidden: scene.isHidden,
            name: scene.composition.name,
            copy: scene.copy,
            status: scene.status,
            stagePositions: scene.stagePositions,
            isGraphicScene: scene.isGraphicScene,
            launchIsRecordingNeeded: scene.launchIsRecordingNeeded,
        };
        return outDTOScene;
    }

    private setNewStagePositionsWithConfigs(stagePositions: IStagePositionsExtended[]): IStagePositionWithConfigs[] {
        if (!stagePositions.length) return [];

        const enriched = stagePositions.map((sp) => ({
            ...sp,
            centerX: sp.x + sp.width / 2,
            centerY: sp.y + sp.height / 2,
        }));
        const stagePositionWithConfigs: IStagePositionWithConfigs[] = [];
        let index = 0;
        let isMainUsed: boolean = false;
        for (const enrichedSP of enriched) {
            const { configs, didUseMain } = this.getStageConfigs(enrichedSP, index, isMainUsed);
            isMainUsed = didUseMain;
            stagePositionWithConfigs.push({ ...enrichedSP, configs: configs });
            index++;
        }

        const order: Record<StagePositionTypeEnum, number> = {
            [StagePositionTypeEnum.MAIN]: 0,
            [StagePositionTypeEnum.SECONDARY]: 1,
            [StagePositionTypeEnum.BACKGROUND]: 2,
        };

        stagePositionWithConfigs.sort((a, b) => order[a.configs.type] - order[b.configs.type]);

        return stagePositionWithConfigs;
    }
    private getStageConfigs(
        pos: IStagePositionsExtended,
        index: number,
        isMainUsed: boolean
    ): { configs: IStagePositionConfigs; didUseMain: boolean } {
        const direction = this.getStageDirection(pos);
        const { type, title, didUseMain } = this.getStagePositionAAAA(direction, isMainUsed);
        return {
            configs: {
                direction,
                matIcon: DirectionToIconMap[direction],
                title: title,
                type: type,
                index,
            },
            didUseMain: didUseMain,
        };
    }

    /**
     * Get the title for a position based on its index
     * @param direction The type of the StagePositionDirectionsEnum (0-based)
     * @returns The title string
     */
    getStagePositionAAAA(
        direction: StagePositionDirectionsEnum,
        isMainUsed: boolean
    ): { title: string; type: StagePositionTypeEnum; didUseMain: boolean } {
        let title: string;
        let type: StagePositionTypeEnum;
        let didUseMain: boolean;
        if (direction === StagePositionDirectionsEnum.BACKGROUND) {
            title = 'Voice Over';
            type = StagePositionTypeEnum.BACKGROUND;
            didUseMain = isMainUsed;
        } else {
            if (isMainUsed) {
                title = 'Secondary Video';
                type = StagePositionTypeEnum.SECONDARY;
                didUseMain = true;
            } else {
                title = 'Main Video';
                type = StagePositionTypeEnum.MAIN;
                didUseMain = true;
            }
        }

        return { title: title, type: type, didUseMain: didUseMain };
    }

    private getStageDirection(pos: IStagePositionsExtended): StagePositionDirectionsEnum {
        // 1️⃣ Background rule
        if (pos.width === 0 && pos.height === 0) {
            return StagePositionDirectionsEnum.BACKGROUND;
        }

        // Compute center point
        const centerX = pos.x + pos.width / 2;
        const centerY = pos.y + pos.height / 2;

        const horizontalThreshold = 10;
        const verticalThreshold = 10;

        // 2️⃣ Check center zone (20% region around middle)
        const isCenter = Math.abs(centerX - 50) <= horizontalThreshold && Math.abs(centerY - 50) <= verticalThreshold;

        if (isCenter) {
            return StagePositionDirectionsEnum.CENTER;
        }

        // 3️⃣ Determine horizontal component
        let horiz: 'LEFT' | 'CENTER' | 'RIGHT';
        if (centerX < 50 - horizontalThreshold) horiz = 'LEFT';
        else if (centerX > 50 + horizontalThreshold) horiz = 'RIGHT';
        else horiz = 'CENTER';

        // 4️⃣ Determine vertical component
        let vert: 'UP' | 'CENTER' | 'DOWN';
        if (centerY < 50 - verticalThreshold) vert = 'UP';
        else if (centerY > 50 + verticalThreshold) vert = 'DOWN';
        else vert = 'CENTER';

        // 5️⃣ Combine into final direction
        if (horiz === 'CENTER' && vert === 'UP') return StagePositionDirectionsEnum.UP;
        if (horiz === 'CENTER' && vert === 'DOWN') return StagePositionDirectionsEnum.DOWN;
        if (horiz === 'LEFT' && vert === 'CENTER') return StagePositionDirectionsEnum.LEFT;
        if (horiz === 'RIGHT' && vert === 'CENTER') return StagePositionDirectionsEnum.RIGHT;

        if (horiz === 'LEFT' && vert === 'UP') return StagePositionDirectionsEnum.LEFT_UP;
        if (horiz === 'LEFT' && vert === 'DOWN') return StagePositionDirectionsEnum.LEFT_DOWN;

        if (horiz === 'RIGHT' && vert === 'UP') return StagePositionDirectionsEnum.RIGHT_UP;
        if (horiz === 'RIGHT' && vert === 'DOWN') return StagePositionDirectionsEnum.RIGHT_DOWN;

        // Fallback
        return StagePositionDirectionsEnum.CENTER;
    }
}
